/**
 * D1 (sub-sistemas, plano `demandas/mapa/SUBSISTEMAS-ANALISE-E-PLANO.md`): sub-sistema é um FILTRO DE MENU. Decide onde cada
 * página (aba) aparece; quem abre a página continua sendo a permissão, e os caminhos (`/<menu>/<slug>/<aba>`) não mudam.
 *
 * Tudo aqui é puro e compartilhado pela API e pelo web (uma regra só: menu, tela inicial, configuração e gerador de caminhos):
 *  - o catálogo (`SUBSISTEMAS`) e a permissão de uso de cada um (`subsistema.<codigo>.usar`);
 *  - a divisão padrão (`DIVISAO_PADRAO`, decisão D1 do Gustavo de 09/10);
 *  - a configuração do cliente, gravada em `configuracoes` só com as DIFERENÇAS do padrão: a do workspace na chave
 *    `nucleo.subsistemas` e a de cada empresa (que sobrepõe a do workspace, decisão D2) em `nucleo.subsistemas.empresa.<id>`;
 *  - a função de visibilidade (`paginaNoSubsistema`) e a lista de páginas sem lugar (`paginasSemLugar`).
 * Sem a configuração ligada (`ativo`), a navegação é a de sempre: nenhum filtro, nenhuma tela de escolha.
 */

export interface SubsistemaCatalogo {
  codigo: string;
  ordem: number;
  /** Reservado: aparece quando o módulo existir (Condominial, PDV); fica fora da configuração e das permissões. */
  reservado?: boolean;
}

export const SUBSISTEMAS: readonly SubsistemaCatalogo[] = [
  // CRM no novo desenho (26/10, decisões 6 e 7): ADM com Cadastros, Assinaturas e recorrência e Portal do cliente (os módulos
  // comuns, Admin, Configurações e Equipe, aparecem em todos). Primeiro no seletor, como no clone do RD.
  { codigo: 'adm', ordem: 5 },
  { codigo: 'erp', ordem: 10 },
  { codigo: 'crm', ordem: 20 },
  { codigo: 'suporte', ordem: 30 },
  { codigo: 'automacoes', ordem: 40 },
  { codigo: 'rh', ordem: 50 },
  { codigo: 'condominial', ordem: 60, reservado: true },
  { codigo: 'pdv', ordem: 70, reservado: true },
];

/** Sub-sistemas em uso (fora os reservados), na ordem do catálogo. */
export const SUBSISTEMAS_ATIVOS: readonly string[] = SUBSISTEMAS.filter((s) => !s.reservado)
  .sort((a, b) => a.ordem - b.ordem)
  .map((s) => s.codigo);

export const permissaoDoSubsistema = (codigo: string) => `subsistema.${codigo}.usar`;

/** Permissões de uso (uma por sub-sistema): entram em todo papel de fábrica pela regra; no personalizado, marcadas. */
export const PERMISSOES_SUBSISTEMAS: readonly string[] = SUBSISTEMAS_ATIVOS.map(permissaoDoSubsistema);

/** Quem edita a configuração (Dono e Administrador pela regra "todas"; qualquer papel com ela marcada, decisão D4). */
export const PERMISSAO_CONFIGURAR_SUBSISTEMAS = 'nucleo.subsistemas.configurar';

/**
 * Módulos comuns a todos os sub-sistemas (Admin e Configurações): fora da configuração, nunca somem. Lista de 30/10 (item 18):
 * o Chat da equipe (menu Comunicação) deixou de ser comum e passou a ser escolhido por sub-sistema como os outros módulos.
 */
export const MODULOS_COMUNS: ReadonlySet<string> = new Set(['admin', 'configuracoes']);

/**
 * Lista de 30/10 (item 18): páginas de módulo comum que moram no menu de outro módulo e, por isso, são escolhidas por
 * sub-sistema: "Usuários e acessos" do Admin fica no menu Pessoas (sem ela fixa, o menu Pessoas deixa de aparecer em todo
 * sub-sistema). O sub-sistema só filtra o menu: quem abre a página continua sendo o papel.
 */
export const PAGINAS_COMUNS_CONFIGURAVEIS: ReadonlySet<string> = new Set(['admin.usuarios']);

/** Quem não usa nenhum sub-sistema continua vendo o Admin, as Configurações e o Chat da equipe (como antes do item 18). */
export const MODULOS_SEM_SUBSISTEMA: ReadonlySet<string> = new Set([...MODULOS_COMUNS, 'equipe']);

/** A página fica fora da configuração (sempre aparece)? Módulo comum, menos as páginas dele que são configuráveis. */
export const paginaComum = (slug: string, aba: string) => MODULOS_COMUNS.has(slug) && !PAGINAS_COMUNS_CONFIGURAVEIS.has(`${slug}.${aba}`);

export const CHAVE_CONFIG_SUBSISTEMAS = 'nucleo.subsistemas';
export const PREFIXO_CONFIG_EMPRESA = 'nucleo.subsistemas.empresa.';
export const chaveConfigEmpresa = (empresaId: string) => `${PREFIXO_CONFIG_EMPRESA}${empresaId}`;

const E = 'erp';
const C = 'crm';
const S = 'suporte';
const A = 'automacoes';
const R = 'rh';
const D = 'adm';

/**
 * Divisão padrão (anexo `subsistemas-proposta.html` e decisão D1 de 09/10): Conversas no CRM e no Suporte; Agenda no CRM;
 * Índices como cadastro do ERP; Quadros no Suporte; Integrações nas Automações; Pessoas, Folha e Treinamento no RH.
 * Chave: slug do módulo, depois a aba. Aba sem padrão aqui (módulo de versão futura) aparece em todos os sub-sistemas.
 */
export const DIVISAO_PADRAO: Readonly<Record<string, Readonly<Record<string, readonly string[]>>>> = {
  cadastros: { 'casca-empresas': [C], 'casca-contatos': [C], pessoas: [D, E, C, S], projetos: [D, C, S], classificacao: [D, C], servicos: [D, E, C], 'servicos-contratados': [D, E], 'formas-pagamento': [D, E], 'condicoes-pagamento': [D, E] },
  financeiro: { 'entradas-saidas': [E], contas: [E], categorias: [E], 'centros-custo': [E], conciliacao: [E], 'fluxo-caixa': [E], indices: [E] },
  contas: { 'a-receber': [E], 'a-pagar': [E], 'notas-credito': [E], recorrencias: [E], cobrancas: [E] },
  relatorios: { inicio: [E], 'painel-grupo': [E], inadimplencia: [E], dre: [E], fluxo: [E], extrato: [E], centros: [E], parceiros: [E], mrr: [E], margem: [E], quadro: [E, R], folha: [E, R], exportacoes: [E] },
  estoque: { produtos: [E], variacoes: [E], depositos: [E], movimentacoes: [E], reposicao: [E], inventario: [E], lotes: [E] },
  compras: { compras: [E], requisicoes: [E], cotacoes: [E], pedidos: [E], recebimentos: [E], fornecedores: [E] },
  pipeline: { pipeline: [C], equipes: [C], metas: [C], 'motivos-perda': [C], atividades: [C], 'casca-comercial': [C], 'casca-atividades': [C], 'casca-relatorios': [C], 'casca-configuracoes': [C] },
  // CRM no novo desenho (decisão 7, 26/10): Assinaturas e Portal saem do CRM para o ADM.
  assinaturas: { contratos: [D], planos: [D], reajustes: [D], onboarding: [D] },
  portal: { acessos: [D], configuracao: [D] },
  conversas: { whatsapp: [C, S], instagram: [C, S], email: [C, S], portal: [C, S], site: [C, S], 'respostas-rapidas': [C, S], templates: [C], filas: [C, S], automacoes: [C, S], 'chat-site': [C, S] },
  agenda: { semana: [C], dia: [C], mes: [C], recursos: [C], 'casca-reunioes': [C] },
  tarefas: { kanban: [S], lista: [S], minhas: [S], painel: [S], quadros: [S], projetos: [S], marcos: [S] },
  integracoes: { conectores: [A], webhooks: [A], api: [A], mcp: [A], 'conta-azul': [A] },
  // FUN 2026-10-15 (U7): o módulo Automações (robôs do servidor Windows) no sub-sistema Automações.
  automacoes: { painel: [A], feriados: [A], fluxos: [A, C], broadcast: [A, C] },
  pessoas: { funcionarios: [R], departamentos: [R], funcoes: [R], contratos: [R], documentos: [R], jornada: [R], habilidades: [R] },
  folha: { beneficios: [R], eventos: [R], competencias: [R], holerites: [R], configuracao: [R] },
  treinamento: { cursos: [R], trilhas: [R], progresso: [R] },
  // Pacote 2 (onda 1a).
  ausencias: { pedidos: [R], aprovacoes: [R], calendario: [R], saldos: [R], tipos: [R] },
  despesas: { despesas: [E], aprovacoes: [E], reembolsos: [E], centros: [E] },
  documentos: { arquivos: [E, C, S], contratos: [E, C], assinaturas: [E, C] },
  // Registro de ponto (lote 1, 19/10).
  ponto: { marcar: [R], marcacoes: [R], espelho: [R], ajustes: [R], banco: [R], fechamentos: [R], fiscal: [R], configuracao: [R] },
  // Lista de 30/10 (item 18): o Chat da equipe (Comunicação) e os Usuários e acessos (menu Pessoas) em todos, como eram; no
  // novo desenho saem do CRM (só as seis páginas da casca, val8) e voltam se o cliente marcar.
  equipe: { chat: [D, E, C, S, A, R] },
  admin: { usuarios: [D, E, C, S, A, R] },
};

/**
 * Val8 (29/10): páginas próprias da casca nova (`paginasCasca` do web: Comercial, Atividades, Reuniões/Agenda, Empresas,
 * Contatos e Configurações do CRM) entram no catálogo da configuração como `<slug>.casca-<chave>`, com o padrão na
 * `DIVISAO_PADRAO` (a lista de páginas configuráveis da API sai daqui; o teste `val8-subsistemas-casca` confere que toda
 * página da casca do web está declarada). Não são abas: não entram no papel (val1); quem abre continua sendo a permissão e a
 * página do papel (`pagina`) de cada uma.
 */
export const PREFIXO_ABA_CASCA = 'casca-';
export const abaDaCasca = (chave: string) => `${PREFIXO_ABA_CASCA}${chave}`;
export const ehAbaDaCasca = (aba: string) => aba.startsWith(PREFIXO_ABA_CASCA);

/** As páginas da casca declaradas na divisão padrão de um módulo (`casca-<chave>`). */
export const abasDaCascaDoModulo = (slug: string): string[] => Object.keys(DIVISAO_PADRAO[slug] ?? {}).filter(ehAbaDaCasca);

/**
 * Val8 (29/10, pedido do Gustavo): com o novo desenho, o CRM tem só as seis páginas da casca. As outras páginas saem do CRM
 * e ficam nos demais sub-sistemas do padrão; a que ficaria sem nenhum vai para o destino abaixo (Pipeline e Agenda no ADM,
 * Templates das Conversas no Suporte). Sem o novo desenho (a casca de sempre não mostra as páginas da casca), vale a
 * divisão padrão de antes, com o CRM inteiro.
 */
export const SEM_CRM_NO_NOVO_DESENHO: Readonly<Record<string, readonly string[]>> = { pipeline: [D], agenda: [D], conversas: [S] };

/** D3: sub-sistema do tipo link externo (abre em nova aba). `papeis` vazio ou ausente: todos do workspace. */
export interface LinkExterno {
  codigo: string;
  nome: string;
  url: string;
  papeis?: string[];
  /**
   * FUN 2026-10-14 (cofre embutido): abre dentro do MalgoOS (página `/externo/<codigo>`, com o menu do sistema e o destino
   * num quadro) em vez de nova aba. Só para endereço de host da lista `HOSTS_EMBUTIVEIS` (`urlEmbutivel`).
   */
  embutir?: boolean;
}

/**
 * FUN 2026-10-14 (cofre embutido): hosts que podem abrir dentro do MalgoOS. Lista fechada no código (nunca qualquer URL: um
 * quadro com site qualquer dentro do sistema vira vetor de clickjacking e phishing). O CSP do web (`frame-src` em
 * `infra/caddy/Caddyfile`) lista os mesmos hosts, conferido por teste; o destino também precisa aceitar ser emoldurado pelo
 * MalgoOS (no Vaultwarden, `ALLOWED_IFRAME_ANCESTORS`). `aviso`: texto de ajuda próprio do destino na página embutida.
 */
export const HOSTS_EMBUTIVEIS: readonly { host: string; aviso?: 'cofre' }[] = [{ host: 'cofre.malgo.com.br', aviso: 'cofre' }];

/** Caminhos do destino que nunca abrem dentro do MalgoOS (o painel de administração do cofre fica só em aba própria). */
const CAMINHOS_NAO_EMBUTIVEIS = /^\/+admin(?:\/|$)/i;

function caminhoDecodificado(pathname: string): string {
  try {
    return decodeURIComponent(pathname);
  } catch {
    return pathname;
  }
}

export type MotivoNaoEmbutivel = 'url' | 'host' | 'caminho';

/** Por que o endereço não pode abrir dentro do MalgoOS (null: pode). */
export function motivoNaoEmbutivel(url: unknown): MotivoNaoEmbutivel | null {
  if (!urlExternaValida(url)) return 'url';
  const u = new URL(url);
  // Porta fora da padrão não casa com o `frame-src` do CSP (que lista o host sem porta).
  if (u.port || !HOSTS_EMBUTIVEIS.some((h) => h.host === u.hostname)) return 'host';
  if (CAMINHOS_NAO_EMBUTIVEIS.test(caminhoDecodificado(u.pathname))) return 'caminho';
  return null;
}

export const urlEmbutivel = (url: unknown): url is string => motivoNaoEmbutivel(url) === null;

/** Aviso próprio do destino embutido (ex.: o do cofre), pelo host. */
export function avisoDoEmbutido(url: string): 'cofre' | null {
  try {
    const host = new URL(url).hostname;
    return HOSTS_EMBUTIVEIS.find((h) => h.host === host)?.aviso ?? null;
  } catch {
    return null;
  }
}

/** D2: ícones nas abas. `trocas`: ícone escolhido pelo cliente por aba (`slug.aba`), módulo (`modulo:slug`) ou grupo (`grupo:menu.grupo`). */
export interface ConfigIcones {
  ativo?: boolean;
  /** Mostrar também no celular (padrão: sim, quando ligado). */
  celular?: boolean;
  trocas?: Record<string, string>;
}

/**
 * Configuração gravada (só as diferenças do padrão). No workspace valem todos os campos; na empresa, só `desligados`,
 * `modulos` e `paginas` (o resto é do workspace).
 */
export interface ConfigSubsistemas {
  /** Workspace: usa sub-sistemas (tela inicial de escolha e menus filtrados). Ausente ou falso: a navegação de sempre. */
  ativo?: boolean;
  /** Sub-sistemas desligados (no workspace, ou nesta empresa). */
  desligados?: string[];
  /** Por sub-sistema, os módulos desligados nele (desliga todas as páginas do módulo ali; religar volta ao que estava). */
  modulos?: Record<string, string[]>;
  /** Por página (`slug.aba`), a escolha por sub-sistema quando difere do padrão. */
  paginas?: Record<string, Record<string, boolean>>;
  icones?: ConfigIcones;
  links?: LinkExterno[];
  /**
   * CRM no novo desenho (N7, 26/10), só no workspace e só com `ativo`: a casca do clone do RD (barra superior com o seletor de
   * sub-sistema e o perfil; barra lateral só com as páginas do sub-sistema), sem a tela de escolha. Ausente ou falso: a casca de sempre.
   */
  novoDesenho?: boolean;
  /**
   * Página Comunicação unificada (04/11, pedido do Gustavo), só no workspace: o menu Comunicação junta o Chat da equipe e os
   * canais do Conversas, em todos os sub-sistemas. Ausente ou falso: a navegação de sempre.
   */
  comunicacaoUnificada?: boolean;
}

export const chavePagina = (slug: string, aba: string) => `${slug}.${aba}`;

/**
 * Sub-sistemas padrão de uma página; sem declaração, todos (página nova nunca fica sem lugar). `novoDesenho` (val8): o CRM só
 * com as páginas da casca (`SEM_CRM_NO_NOVO_DESENHO` para a que ficaria sem lugar).
 */
export function padraoDaPagina(slug: string, aba: string, novoDesenho = false): readonly string[] {
  const padrao = DIVISAO_PADRAO[slug]?.[aba] ?? SUBSISTEMAS_ATIVOS;
  if (!novoDesenho || ehAbaDaCasca(aba) || !padrao.includes(C)) return padrao;
  const semCrm = padrao.filter((x) => x !== C);
  return semCrm.length ? semCrm : (SEM_CRM_NO_NOVO_DESENHO[slug] ?? SUBSISTEMAS_ATIVOS.filter((x) => x !== C));
}

const RE_CODIGO = /^[a-z][a-z0-9-]{0,39}$/;
const RE_ICONE = /^[A-Z][A-Za-z0-9]{1,48}$/;
const RE_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const RE_CHAVE_ICONE = /^(?:[a-z][a-z0-9-]{0,39}\.[a-z][a-z0-9-]{0,39}|modulo:[a-z][a-z0-9-]{0,39}|grupo:[a-z][a-z0-9-]{0,39}\.[a-z][a-z0-9-]{0,39})$/;

/** URL de link externo aceita: só https, sem usuário e senha na URL. */
export function urlExternaValida(url: unknown): url is string {
  if (typeof url !== 'string' || url.length > 500) return false;
  try {
    const u = new URL(url);
    return u.protocol === 'https:' && !u.username && !u.password && !!u.hostname;
  } catch {
    return false;
  }
}

const objeto = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);

/**
 * Review r38 robustez (B1): tetos das partes que são mapas (a config inteira vai no `/auth/sessao` de todo membro). Ficam bem
 * acima do catálogo (cerca de 150 páginas e 250 chaves de ícone com todos os módulos).
 */
export const MAX_PAGINAS_CONFIG = 600;
export const MAX_TROCAS_ICONE = 600;

/**
 * Lê o valor gravado (ou vindo de fora) e devolve só o que é válido: código de sub-sistema do catálogo, página no formato
 * `slug.aba`, ícone com nome de componente, link https. O que não casa é descartado (nunca quebra a navegação).
 * `paginas`: as páginas que existem (`slug.aba` dos módulos instalados). Com ela (a gravação, `subsistemas.definir`), a
 * página e a troca de ícone de aba ou de módulo que não existem saem; sem ela (a leitura), só os tetos.
 */
export function normalizarConfig(v: unknown, opcoes: { paginas?: ReadonlySet<string> } = {}): ConfigSubsistemas {
  if (!objeto(v)) return {};
  const paginaExiste = (chave: string) => !opcoes.paginas || opcoes.paginas.has(chave);
  const modulosExistentes = opcoes.paginas ? new Set([...opcoes.paginas].map((p) => p.split('.')[0]!)) : null;
  const chaveDeIconeExiste = (k: string) =>
    k.startsWith('modulo:') ? !modulosExistentes || modulosExistentes.has(k.slice('modulo:'.length)) : k.startsWith('grupo:') ? true : paginaExiste(k);
  const ativos = new Set(SUBSISTEMAS_ATIVOS);
  const saida: ConfigSubsistemas = {};
  if (typeof v.ativo === 'boolean') saida.ativo = v.ativo;
  if (typeof v.novoDesenho === 'boolean') saida.novoDesenho = v.novoDesenho;
  if (typeof v.comunicacaoUnificada === 'boolean') saida.comunicacaoUnificada = v.comunicacaoUnificada;
  if (Array.isArray(v.desligados)) {
    const d = [...new Set(v.desligados.filter((x): x is string => typeof x === 'string' && ativos.has(x)))].sort();
    // Lista vazia vale (a empresa religa tudo o que o workspace desligou); no workspace, `compactarConfig` a tira.
    saida.desligados = d;
  }
  if (objeto(v.modulos)) {
    const m: Record<string, string[]> = {};
    for (const [sub, lista] of Object.entries(v.modulos)) {
      if (!ativos.has(sub) || !Array.isArray(lista)) continue;
      const slugs = [...new Set(lista.filter((x): x is string => typeof x === 'string' && RE_CODIGO.test(x) && !MODULOS_COMUNS.has(x)))].sort();
      m[sub] = slugs;
    }
    if (Object.keys(m).length) saida.modulos = m;
  }
  if (objeto(v.paginas)) {
    const p: Record<string, Record<string, boolean>> = {};
    for (const [chave, porSub] of Object.entries(v.paginas)) {
      const [slug, aba, ...resto] = chave.split('.');
      if (Object.keys(p).length >= MAX_PAGINAS_CONFIG) break;
      if (!slug || !aba || resto.length || !RE_CODIGO.test(slug) || !RE_CODIGO.test(aba) || paginaComum(slug, aba) || !objeto(porSub) || !paginaExiste(chave)) continue;
      const linha: Record<string, boolean> = {};
      for (const [sub, liga] of Object.entries(porSub)) if (ativos.has(sub) && typeof liga === 'boolean') linha[sub] = liga;
      if (Object.keys(linha).length) p[chave] = linha;
    }
    if (Object.keys(p).length) saida.paginas = p;
  }
  if (objeto(v.icones)) {
    const i: ConfigIcones = {};
    if (typeof v.icones.ativo === 'boolean') i.ativo = v.icones.ativo;
    if (typeof v.icones.celular === 'boolean') i.celular = v.icones.celular;
    if (objeto(v.icones.trocas)) {
      const tr: Record<string, string> = {};
      for (const [k, nome] of Object.entries(v.icones.trocas)) {
        if (Object.keys(tr).length >= MAX_TROCAS_ICONE) break;
        if (RE_CHAVE_ICONE.test(k) && typeof nome === 'string' && RE_ICONE.test(nome) && chaveDeIconeExiste(k)) tr[k] = nome;
      }
      if (Object.keys(tr).length) i.trocas = tr;
    }
    if (Object.keys(i).length) saida.icones = i;
  }
  if (Array.isArray(v.links)) {
    const vistos = new Set<string>();
    const links: LinkExterno[] = [];
    for (const l of v.links) {
      if (!objeto(l) || typeof l.codigo !== 'string' || !RE_CODIGO.test(l.codigo) || vistos.has(l.codigo) || ativos.has(l.codigo)) continue;
      const nome = typeof l.nome === 'string' ? l.nome.trim().slice(0, 60) : '';
      if (!nome || !urlExternaValida(l.url)) continue;
      vistos.add(l.codigo);
      const papeis = Array.isArray(l.papeis) ? [...new Set(l.papeis.filter((x): x is string => typeof x === 'string' && RE_UUID.test(x)).map((x) => x.toLowerCase()))] : [];
      // FUN 2026-10-14: `embutir` só fica com endereço de host permitido (gravado antes de o host sair da lista: abre em nova aba).
      const embutir = l.embutir === true && urlEmbutivel(l.url);
      links.push({ codigo: l.codigo, nome, url: l.url, ...(papeis.length ? { papeis } : {}), ...(embutir ? { embutir: true } : {}) });
      if (links.length >= 20) break;
    }
    if (links.length) saida.links = links;
  }
  return saida;
}

/** Só o que a empresa pode sobrepor (`desligados`, `modulos`, `paginas`). */
export function soDaEmpresa(c: ConfigSubsistemas): ConfigSubsistemas {
  const { desligados, modulos, paginas } = c;
  return { ...(desligados ? { desligados } : {}), ...(modulos ? { modulos } : {}), ...(paginas ? { paginas } : {}) };
}

/**
 * Configuração efetiva numa empresa: a do workspace com a da empresa por cima (decisão D2). A lista de desligados e os
 * módulos de cada sub-sistema da empresa substituem os do workspace; as páginas se somam (a escolha da empresa vence).
 */
export function combinarConfig(workspace: ConfigSubsistemas, empresa?: ConfigSubsistemas | null): ConfigSubsistemas {
  if (!empresa) return workspace;
  const modulos = { ...(workspace.modulos ?? {}), ...(empresa.modulos ?? {}) };
  const paginas: Record<string, Record<string, boolean>> = {};
  for (const [k, v] of Object.entries(workspace.paginas ?? {})) paginas[k] = { ...v };
  for (const [k, v] of Object.entries(empresa.paginas ?? {})) paginas[k] = { ...(paginas[k] ?? {}), ...v };
  return { ...workspace, desligados: empresa.desligados ?? workspace.desligados, modulos, paginas };
}

/** N7: a casca do novo desenho vale agora (sub-sistemas ligados e a chave do workspace ligada). */
export const novoDesenhoLigado = (c: ConfigSubsistemas) => !!c.ativo && !!c.novoDesenho;

/** A página Comunicação unificada vale neste workspace. */
export const comunicacaoLigada = (c: ConfigSubsistemas) => !!c.comunicacaoUnificada;

/** Página que, com a Comunicação unificada, aparece em todo sub-sistema: o Chat da equipe e todas as do Conversas. */
export const paginaDaComunicacao = (slug: string, aba: string) => (slug === 'equipe' && aba === 'chat') || slug === 'conversas';

export const subsistemaLigado = (c: ConfigSubsistemas, sub: string) => SUBSISTEMAS_ATIVOS.includes(sub) && !c.desligados?.includes(sub);

/**
 * A página aparece no sub-sistema? (regra 3 e 4 da seção 3.2 do plano; a permissão da aba, o módulo instalado e quem usa o
 * sub-sistema são conferidos por quem chama). Módulo comum: sempre. Módulo desligado no sub-sistema: não. Senão a escolha
 * do cliente para a página, ou o padrão.
 */
export function paginaNoSubsistema(c: ConfigSubsistemas, sub: string, slug: string, aba: string): boolean {
  if (paginaComum(slug, aba)) return true;
  if (comunicacaoLigada(c) && paginaDaComunicacao(slug, aba)) return true;
  if (c.modulos?.[sub]?.includes(slug)) return false;
  const escolha = c.paginas?.[chavePagina(slug, aba)]?.[sub];
  if (escolha !== undefined) return escolha;
  return padraoDaPagina(slug, aba, novoDesenhoLigado(c)).includes(sub);
}

/** A página (fora dos módulos comuns) aparece em pelo menos um sub-sistema ligado? */
export function paginaTemLugar(c: ConfigSubsistemas, slug: string, aba: string): boolean {
  if (paginaComum(slug, aba)) return true;
  return SUBSISTEMAS_ATIVOS.some((sub) => subsistemaLigado(c, sub) && paginaNoSubsistema(c, sub, slug, aba));
}

/**
 * Val8: as páginas da configuração que valem nesta casca. As da casca nova (`casca-<chave>`) só com o novo desenho: na casca
 * de sempre elas não aparecem no menu, então ficam fora da tela e da conta das páginas sem lugar.
 */
export function paginasDaConfig<P extends { aba: string }>(c: ConfigSubsistemas, paginas: readonly P[]): P[] {
  return novoDesenhoLigado(c) ? [...paginas] : paginas.filter((p) => !ehAbaDaCasca(p.aba));
}

/** Páginas que ficam fora de todos os sub-sistemas ligados (pedem a confirmação com consentimento antes de gravar). */
export function paginasSemLugar(c: ConfigSubsistemas, paginas: readonly { slug: string; aba: string }[]): { slug: string; aba: string }[] {
  return paginasDaConfig(c, paginas).filter((p) => !paginaComum(p.slug, p.aba) && !paginaTemLugar(c, p.slug, p.aba));
}

/**
 * Compacta a configuração para gravar só as diferenças: a escolha de página igual à base (o padrão no workspace; o efeito do
 * workspace na empresa) sai; listas vazias saem. `base` é a configuração do workspace quando o escopo é uma empresa.
 */
export function compactarConfig(c: ConfigSubsistemas, base?: ConfigSubsistemas): ConfigSubsistemas {
  const n = normalizarConfig(c);
  if (!base) {
    // No workspace a lista vazia é o padrão: não precisa ser gravada.
    if (n.desligados && !n.desligados.length) delete n.desligados;
    if (n.modulos) {
      for (const [sub, l] of Object.entries(n.modulos)) if (!l.length) delete n.modulos[sub];
      if (!Object.keys(n.modulos).length) delete n.modulos;
    }
  }
  if (!n.paginas) return n;
  const ref = base ?? {};
  // Val8: o padrão do workspace com ou sem o novo desenho (na empresa, o do workspace).
  const novo = novoDesenhoLigado(base ?? n);
  const paginas: Record<string, Record<string, boolean>> = {};
  for (const [chave, porSub] of Object.entries(n.paginas)) {
    const [slug, aba] = chave.split('.') as [string, string];
    const linha: Record<string, boolean> = {};
    for (const [sub, liga] of Object.entries(porSub)) {
      const daBase = ref.paginas?.[chave]?.[sub] ?? padraoDaPagina(slug, aba, novo).includes(sub);
      if (liga !== daBase) linha[sub] = liga;
    }
    if (Object.keys(linha).length) paginas[chave] = linha;
  }
  const { paginas: _fora, ...resto } = n;
  return Object.keys(paginas).length ? { ...resto, paginas } : resto;
}
